import { useState, useMemo } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Pencil, Trash2, Plane, Clock, Timer, Paperclip } from "lucide-react";
import { FlightSegment } from "@/hooks/useTrips";
import { EditFlightDialog } from "../dialogs/EditFlightDialog";
import { DeleteItemDialog } from "../dialogs/DeleteItemDialog";
import { AttachmentsSheet, AttachmentsBadge, SourceEmailBadge } from "../attachments";
import { formatFlightTime, calculateFlightDuration, getTimezoneAbbreviation, formatDurationMinutes, getAirportTimezone, safeFormat } from "@/lib/formatDateTime";
// Native date helpers to avoid date-fns locale issues
function safeParseDateString(dateStr: string | undefined | null): Date | null {
  if (!dateStr || typeof dateStr !== 'string') return null;
  try {
    const date = new Date(dateStr);
    if (isNaN(date.getTime())) return null;
    return date;
  } catch {
    return null;
  }
}

function isPastDate(date: Date): boolean {
  return date.getTime() < Date.now();
}

function diffInMinutes(later: Date, earlier: Date): number {
  return Math.round((later.getTime() - earlier.getTime()) / (1000 * 60));
}

function diffInHours(later: Date, earlier: Date): number {
  return Math.floor((later.getTime() - earlier.getTime()) / (1000 * 60 * 60));
}
import { getAirlineLogoUrlString } from "@/lib/companyLogos";

interface FlightCardProps {
  flight: FlightSegment;
  allFlights?: FlightSegment[];
  tripId?: string;
  index?: number;
  readOnly?: boolean;
  tripPassengerName?: string;
}

type DisplayPassenger = {
  name: string;
  confirmation_number?: string;
  seat_number?: string;
  ticket_number?: string;
};

function parseTripPassengerNames(passengerName: string | undefined): string[] {
  if (!passengerName) return [];
  return passengerName
    .split(/,|;|&|\band\b|\/|\n/gi)
    .map((name) => name.trim())
    .filter((name) => name.length > 0);
}

function getFlightBookingRefs(segment: FlightSegment): Set<string> {
  const refs = new Set<string>();
  const addRef = (value?: string) => {
    if (!value) return;
    const normalized = value.trim().toLowerCase();
    if (normalized) refs.add(normalized);
  };

  addRef(segment.confirmation_number);
  (segment.additional_confirmation_numbers || []).forEach(addRef);
  (segment.passengers || []).forEach((p) => addRef(p.confirmation_number));

  return refs;
}

function getFlightPassengerNameSet(segment: FlightSegment): Set<string> {
  const names = new Set<string>();
  (segment.passengers || []).forEach((p) => {
    if (!p.name) return;
    const normalized = p.name.trim().toLowerCase();
    if (normalized) names.add(normalized);
  });
  return names;
}

// Status badge styling
function getStatusBadge(status: string | undefined) {
  if (!status) return null;
  
  const statusLower = status.toLowerCase();
  let variant: "default" | "secondary" | "destructive" | "outline" = "secondary";
  let className = "";
  
  if (statusLower === "confirmed" || statusLower === "scheduled") {
    variant = "default";
    className = "bg-green-600 hover:bg-green-700";
  } else if (statusLower === "active" || statusLower === "in air" || statusLower === "in_air") {
    variant = "default";
    className = "bg-blue-600 hover:bg-blue-700";
  } else if (statusLower === "landed") {
    variant = "secondary";
    className = "bg-gray-500 hover:bg-gray-600 text-white";
  } else if (statusLower === "delayed") {
    variant = "destructive";
    className = "bg-orange-500 hover:bg-orange-600";
  } else if (statusLower === "cancelled") {
    variant = "destructive";
  }
  
  return (
    <Badge variant={variant} className={className}>
      {status}
    </Badge>
  );
}

// Calculate countdown to departure
function getDepartureCountdown(departureTime: string | undefined): { text: string; isUrgent: boolean } | null {
  if (!departureTime) return null;

  try {
    const departure = safeParseDateString(departureTime);
    if (!departure) return null;

    const now = new Date();

    if (isPastDate(departure)) {
      return null; // Flight has departed
    }

    const hoursUntil = diffInHours(departure, now);
    const minutesUntil = diffInMinutes(departure, now) % 60;

    if (hoursUntil > 48) {
      return null; // More than 48 hours away, don't show countdown
    }

    if (hoursUntil >= 24) {
      const days = Math.floor(hoursUntil / 24);
      const remainingHours = hoursUntil % 24;
      return {
        text: `${days}d ${remainingHours}h until departure`,
        isUrgent: false
      };
    }

    if (hoursUntil >= 1) {
      return {
        text: `${hoursUntil}h ${minutesUntil}m until departure`,
        isUrgent: hoursUntil <= 3
      };
    }

    return {
      text: `${minutesUntil}m until departure`,
      isUrgent: true
    };
  } catch {
    return null;
  }
}

export const FlightCard = ({ flight, allFlights, tripId, index, readOnly = false, tripPassengerName }: FlightCardProps) => {
  const [showEdit, setShowEdit] = useState(false);
  const [showDelete, setShowDelete] = useState(false);
  const [showAttachments, setShowAttachments] = useState(false);
  const [expanded, setExpanded] = useState(false);

  // Derive timezones from airport codes if not explicitly provided
  const depTimezone = flight.departure_timezone || getAirportTimezone(flight.departure_airport);
  const arrTimezone = flight.arrival_timezone || getAirportTimezone(flight.arrival_airport);

  // Prefer API-provided duration_minutes, fall back to calculated duration with timezone awareness
  const duration = flight.duration_minutes
    ? formatDurationMinutes(flight.duration_minutes)
    : calculateFlightDuration(
        flight.departure_time,
        flight.arrival_time,
        depTimezone,
        arrTimezone
      );
  const depTz = depTimezone ? getTimezoneAbbreviation(depTimezone) : null;
  const arrTz = arrTimezone ? getTimezoneAbbreviation(arrTimezone) : null;

  const countdown = useMemo(() => getDepartureCountdown(flight.departure_time), [flight.departure_time]);

  // Get logo URL - try Duffel first (high-quality SVG), then logo.dev
  const logoUrl = useMemo(() => {
    if (flight.airline) return getAirlineLogoUrlString(flight.airline, 64);
    return null;
  }, [flight.airline]);

  // Check if there are expandable details to show
  const hasExpandableDetails = !!(
    flight.operated_by ||
    flight.legroom ||
    flight.meals ||
    (flight.additional_confirmation_numbers && flight.additional_confirmation_numbers.length > 0) ||
    (flight.passengers && flight.passengers.length > 0) ||
    parseTripPassengerNames(tripPassengerName).length > 0
  );

  const displayPassengers: DisplayPassenger[] = useMemo(() => {
    const merged = (flight.passengers || []).map((p) => ({
      name: p.name,
      confirmation_number: p.confirmation_number,
      seat_number: p.seat_number,
      ticket_number: p.ticket_number,
    }));

    const knownNames = new Set(merged.map((p) => p.name.trim().toLowerCase()));
    const currentRefs = getFlightBookingRefs(flight);

    // Fill missing passenger entries from other segments in the same trip
    // when booking references overlap (same confirmation chain).
    if (allFlights && allFlights.length > 1) {
      const currentNames = getFlightPassengerNameSet(flight);
      allFlights.forEach((segment) => {
        if (segment === flight || !segment.passengers?.length) return;
        const segmentRefs = getFlightBookingRefs(segment);
        const segmentNames = getFlightPassengerNameSet(segment);
        const hasSharedRefs = Array.from(segmentRefs).some((ref) => currentRefs.has(ref));
        const hasSharedPassengerNames = Array.from(segmentNames).some((name) => currentNames.has(name));
        const isRelated = hasSharedRefs || hasSharedPassengerNames;
        if (!isRelated) return;

        segment.passengers.forEach((p) => {
          const key = p.name.trim().toLowerCase();
          if (!p.name || knownNames.has(key)) return;
          merged.push({
            name: p.name,
            confirmation_number: p.confirmation_number,
            seat_number: p.seat_number,
            ticket_number: p.ticket_number,
          });
          knownNames.add(key);
        });
      });

    }

    const fallbackNames = parseTripPassengerNames(tripPassengerName);

    fallbackNames.forEach((name) => {
      const key = name.trim().toLowerCase();
      if (!knownNames.has(key)) {
        merged.push({ name });
        knownNames.add(key);
      }
    });

    return merged;
  }, [flight, allFlights, tripPassengerName]);

  // Format time for display (HH:MM)
  const getTimeDisplay = (timeStr: string | undefined) => {
    if (!timeStr) return '--:--';
    try {
      const date = safeParseDateString(timeStr);
      if (date) {
        return date.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', hour12: false });
      }
      // Try to extract time from string like "14:30"
      const match = timeStr.match(/(\d{1,2}):(\d{2})/);
      if (match) return `${match[1].padStart(2, '0')}:${match[2]}`;
      return '--:--';
    } catch {
      // Try to extract time from string like "14:30"
      const match = timeStr.match(/(\d{1,2}):(\d{2})/);
      if (match) return `${match[1].padStart(2, '0')}:${match[2]}`;
      return '--:--';
    }
  };

  return (
    <>
      <Card variant="interactive">
        <CardContent className="p-6">
          {/* Header with airline info and actions */}
          <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between mb-4">
            <div className="flex items-center gap-3">
              {logoUrl ? (
                <div className="w-12 h-12 rounded bg-blue-50 border border-blue-200 flex items-center justify-center overflow-hidden">
                  <img src={logoUrl} alt={flight.airline} className="w-8 h-8 object-contain mix-blend-multiply" />
                </div>
              ) : (
                <div className="w-12 h-12 rounded bg-blue-50 border border-blue-200 flex items-center justify-center">
                  <span className="text-xs font-bold text-blue-600">
                    {flight.airline_code || flight.airline?.substring(0, 2).toUpperCase()}
                  </span>
                </div>
              )}
              <div>
                <div className="flex items-center gap-2 mb-1">
                  <h3 className="font-semibold text-lg">
                    {flight.airline} {flight.flight_number}
                  </h3>
                  {getStatusBadge(flight.status)}
                </div>
                <p className="text-sm text-muted-foreground">
                  {flight.departure_airport_code || flight.departure_airport?.match(/\(([A-Z]{3})\)/)?.[1] || flight.departure_airport} → {flight.arrival_airport_code || flight.arrival_airport?.match(/\(([A-Z]{3})\)/)?.[1] || flight.arrival_airport}
                </p>
              </div>
            </div>
            {!readOnly && tripId !== undefined && index !== undefined && (
              <div className="flex items-center gap-2">
                <AttachmentsBadge
                  tripId={tripId}
                  itemType="flights"
                  itemIndex={index}
                  onClick={() => setShowAttachments(true)}
                />
                <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                  {flight.source_email_id && (
                    <SourceEmailBadge onClick={() => setShowAttachments(true)} />
                  )}
                  <Button variant="ghost" size="icon" onClick={() => setShowAttachments(true)} title="Attachments">
                    <Paperclip className="h-4 w-4" />
                  </Button>
                  <Button variant="ghost" size="icon" onClick={() => setShowEdit(true)}>
                    <Pencil className="h-4 w-4" />
                  </Button>
                  <Button variant="ghost" size="icon" onClick={() => setShowDelete(true)}>
                    <Trash2 className="h-4 w-4 text-destructive" />
                  </Button>
                </div>
              </div>
            )}
          </div>

          {/* Departure Countdown */}
          {countdown && (
            <div className={`flex items-center gap-1.5 text-sm font-medium mb-4 ${countdown.isUrgent ? 'text-orange-600' : 'text-blue-600'}`}>
              <Timer className="h-3.5 w-3.5" />
              {countdown.text}
            </div>
          )}

          {/* Flight Route - 3 Column Layout */}
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-4">
            <div>
              {(flight.departure_date || flight.departure_time) && (
                <div className="text-sm font-medium text-muted-foreground mb-1">
                  {safeFormat(flight.departure_date || flight.departure_time, "EEE, MMM d, yyyy")}
                </div>
              )}
              <div className="text-2xl font-semibold mb-1">{getTimeDisplay(flight.departure_time)}</div>
              <div className="text-sm text-muted-foreground">{flight.departure_airport}</div>
              <div className="text-xs text-muted-foreground">{flight.departure_airport_code}</div>
              {flight.departure_terminal && (
                <div className="text-xs text-muted-foreground mt-1">Terminal {flight.departure_terminal}</div>
              )}
              {flight.departure_gate && (
                <div className="text-xs text-muted-foreground">Gate {flight.departure_gate}</div>
              )}
            </div>

            <div className="flex flex-col items-center justify-center">
              <div className="w-full border-t-2 border-dashed border-border relative">
                <Plane className="w-4 h-4 text-muted-foreground absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 bg-card rotate-90" />
              </div>
              <div className="text-xs text-muted-foreground mt-2">{duration || 'N/A'}</div>
              <div className="text-xs text-muted-foreground">
                {flight.stops === 0 || flight.stops === undefined ? 'Nonstop' : `${flight.stops} stop${flight.stops > 1 ? 's' : ''}`}
              </div>
            </div>

            <div className="text-right">
              {(flight.arrival_date || flight.arrival_time) && (
                <div className="text-sm font-medium text-muted-foreground mb-1">
                  {safeFormat(flight.arrival_date || flight.arrival_time, "EEE, MMM d, yyyy")}
                </div>
              )}
              <div className="text-2xl font-semibold mb-1">{getTimeDisplay(flight.arrival_time)}</div>
              <div className="text-sm text-muted-foreground">{flight.arrival_airport}</div>
              <div className="text-xs text-muted-foreground">{flight.arrival_airport_code}</div>
              {flight.arrival_terminal && (
                <div className="text-xs text-muted-foreground mt-1">Terminal {flight.arrival_terminal}</div>
              )}
            </div>
          </div>

          {/* Quick Info */}
          <div className="flex flex-wrap gap-4 text-sm text-muted-foreground mb-4">
            {flight.aircraft && (
              <div className="flex items-center gap-1">
                <Plane className="w-4 h-4" />
                {flight.aircraft}
              </div>
            )}
            {flight.cabin_class && (
              <div className="flex items-center gap-1">
                <span className="font-medium">Class:</span>
                {flight.cabin_class}
              </div>
            )}
            {flight.confirmation_number && (
              <div className="flex items-center gap-1">
                <span className="font-medium">Conf:</span>
                {flight.confirmation_number}
              </div>
            )}
          </div>

          {/* Delay Warning */}
          {flight.delay && flight.delay > 0 && (
            <div className="text-sm text-destructive font-medium mb-4">
              ⚠️ Delayed: {flight.delay} minutes
            </div>
          )}
          {flight.often_delayed && (
            <div className="text-sm text-orange-600 font-medium mb-4">
              Often delayed by 30+ min
            </div>
          )}

          {/* Expandable Details */}
          {expanded && (
            <div className="border-t border-border pt-4 space-y-3">
              {flight.operated_by && (
                <div>
                  <span className="text-sm font-medium">Operated by:</span>
                  <span className="text-sm text-muted-foreground ml-2">{flight.operated_by}</span>
                </div>
              )}
              {flight.legroom && (
                <div>
                  <span className="text-sm font-medium">Legroom:</span>
                  <span className="text-sm text-muted-foreground ml-2">{flight.legroom}</span>
                </div>
              )}
              {flight.meals && (
                <div>
                  <span className="text-sm font-medium">Meals:</span>
                  <span className="text-sm text-muted-foreground ml-2">{flight.meals}</span>
                </div>
              )}
              {flight.additional_confirmation_numbers && flight.additional_confirmation_numbers.length > 0 && (
                <div>
                  <span className="text-sm font-medium">Additional Confirmations:</span>
                  <span className="text-sm text-muted-foreground ml-2">
                    {flight.additional_confirmation_numbers.join(", ")}
                  </span>
                </div>
              )}
              {displayPassengers.length > 0 && (
                <div>
                  <span className="text-sm font-medium">Passengers:</span>
                  <div className="mt-1 space-y-1">
                    {displayPassengers.map((p, i) => (
                      <div key={i} className="text-sm text-muted-foreground ml-2 flex flex-wrap gap-x-3">
                        <span className="font-medium text-foreground">{p.name}</span>
                        {p.confirmation_number && <span>Conf: {p.confirmation_number}</span>}
                        {p.seat_number && <span>Seat {p.seat_number}</span>}
                        {p.ticket_number && <span>Ticket: {p.ticket_number}</span>}
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Toggle Button - only show if there are details to expand */}
          {hasExpandableDetails && (
            <button
              onClick={() => setExpanded(!expanded)}
              className="w-full mt-4 text-sm text-accent hover:text-accent/80 transition-colors"
            >
              {expanded ? 'Show less' : 'Show more details'}
            </button>
          )}
        </CardContent>
      </Card>

      {!readOnly && tripId !== undefined && index !== undefined && (
        <>
          <EditFlightDialog
            flight={flight}
            tripId={tripId}
            index={index}
            open={showEdit}
            onOpenChange={setShowEdit}
          />
          <DeleteItemDialog
            tripId={tripId}
            itemType="flights"
            index={index}
            itemName={`${flight.airline} ${flight.flight_number}`}
            open={showDelete}
            onOpenChange={setShowDelete}
          />
          <AttachmentsSheet
            open={showAttachments}
            onOpenChange={setShowAttachments}
            tripId={tripId}
            itemType="flights"
            itemIndex={index}
            itemTitle={`${flight.airline} ${flight.flight_number}`}
            sourceEmailId={flight.source_email_id}
          />
        </>
      )}
    </>
  );
};
